class SearchForm extends HTMLElement {
  constructor() {
    super();

    // DOM Elements
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    // Event Listeners
    if (this.input) {
      // Form reset handler (when user clicks reset or presses ESC)
      this.input.form.addEventListener("reset", this.onFormReset.bind(this));

      // Input handler with debouncing to prevent excessive events
      this.input.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  /**
   * Toggles the reset button's visibility based on input content
   */
  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains("hidden");

    // Show reset button when there's text, hide when empty
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove("hidden");
      this.input.classList.add("active");
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add("hidden");
      this.input.classList.remove("active");
    }
  }

  // Handles input changes - primary function is to manage reset button
  onChange() {
    this.toggleResetButton();
  }

  /**
   * Determines if form should fully reset
   * Returns false if user has selected a predictive search result
   */
  shouldResetForm() {
    // Checks for selected items in predictive search results
    return !document.querySelector('[aria-selected="true"] a');
  }

  /**
   * Form reset handler with predictive search consideration
   */
  onFormReset(e) {
    // Prevent default to avoid URL parameters affecting the reset
    e.preventDefault();

    // Only reset if no predictive search item is selected
    if (this.shouldResetForm()) {
      this.input.value = "";
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

// Register the custom element
customElements.define("search-form", SearchForm);

class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    // State management
    this.cachedResults = {}; // Cache for storing search results
    this.isOpen = false; // Dropdown visibility state
    this.abortController = new AbortController(); // For canceling fetch requests
    this.searchTerm = ""; // Current search term

    this.statusElement = this.querySelector(".predictive-search-status");
    this.header = document.getElementById("main-header");
    this.mainOverlay = document.querySelector(".main__overlay");
    this.searchForm = document.getElementById("header__search");

    // Results container
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");

    this.setupEventListeners();

    // recommendation - 1
    this.showingRecommendations = false;
    // this.initializeRecommendations();
  }

  setupEventListeners() {
    // Form submission handler
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    // Focus/blur handlers
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));

    // Keyboard navigation
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    // Clear irrelevant results when search term changes significantly
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update UI for new term immediately
    // this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    // Close if empty
    if (!this.searchTerm.length) {
      // this.close(true);
      // return;

      // recommendation - 2 Show recommendations when empty
      this.showRecommendations();
      return;
    }

    // Only search if there's a term
    // this.showingRecommendations = false;

    // Fetch new results
    this.getSearchResults(this.searchTerm);
  }

  // Prevents default if no query or if a result is selected
  onFormSubmit(e) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      e.preventDefault();
    }

    // Close search overlay after submission
    this.closeSearchOverlay();
  }

  // Handle form reset (extends parent class)
  onFormReset(e) {
    super.onFormReset(e);

    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort(); // Cancel pending requests
      this.abortController = new AbortController(); // New controller
      this.closeResults(true);
    }
  }

  // handle focus out - 1(no result so do nothing) -2(there were results so just open) -3(normal fetching results)
  onFocus() {
    const currentSearchTerm = this.getQuery();

    // recommendation - 3
    if (!currentSearchTerm.length) {
      // Always show recommendations when focusing on empty input
      this.showRecommendations();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Term changed externally - treat as new search
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      // Already have results - just open
      this.open();
    } else {
      // Fetch fresh results
      this.getSearchResults(this.searchTerm);
    }
  }

  showRecommendations() {
    this.setAttribute("results", "false");
    this.open();
  }

  // Handle focus leaving the component keep results but close predictive search
  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  // New method to handle closing the search overlay
  closeSearchOverlay() {
    closeSearch();
  }

  // Handle keyup events for navigation
  onKeyup(e) {
    if (!this.getQuery().length) this.close(true);
    e.preventDefault();

    switch (e.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(e) {
    // Pre the cursor from moving in the input when using the up and down arrow keys
    if (e.code === "ArrowUp" || e.code === "ArrowDown") {
      e.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );

    // defensive checks - return if element doesn't exist or has no text
    if (!searchForTextElement || !searchForTextElement?.innerText) return;

    const currentButtonText = searchForTextElement.innerText;

    // Only proceed if we have both terms and they're different
    if (!previousTerm || !newTerm || previousTerm === newTerm) return;

    try {
      // Only replace if the term appears once (avoid partial matches in text)
      const matches = currentButtonText.match(new RegExp(previousTerm, "g"));
      if (!matches || matches.length > 1) return;

      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    } catch (e) {
      console.error("Error updating search term text:", e);
    }
  }

  // Move selection up/down in results if it exist only
  switchOption(direction) {
    // 1. Exit if dropdown closed or no results
    if (!this.getAttribute("open") || !this.getAttribute("results")) return;

    // 2. Initialize status element and Ensure status element exists
    if (!this.statusElement) {
      this.statusElement = this.querySelector(".predictive-search-status");
      if (!this.statusElement) return; // Still not found, exit
    }

    // 3. Determine direction
    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // 4. Get visible, focusable elements
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);

    // Exit if no visible elements
    if (allVisibleElements.length === 0) return;

    let activeElementIndex = 0;

    // 4. Find current selection index
    let selectedElementIndex = -1;
    let i = 0;
    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    // 5. Clear previous status announcements
    this.statusElement.textContent = "";

    // 6. Calculate new index
    if (!moveUp && selectedElement) {
      // Move down (with wrap-around)
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      // Move up (with wrap-around)
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    // 7. Skip if no change
    if (activeElementIndex === selectedElementIndex) return;

    // 8. Update selection
    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    // 9. Update aria-activedescendant
    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    // 1. Create cache key
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();

    // 2. Show loading state
    this.setLiveRegionLoadingState();

    // 3. Check cache first
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // 4. Fetch new results
    fetch(
      `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(
        searchTerm
      )}&resources[type]=product,article,page&section_id=predictive-search`,
      {
        signal: this.abortController.signal, // Attach abort control
      }
    )
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text(); // Get HTML string
      })
      .then((text) => {
        // 5. Parse HTML response
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;

        // 6. Update all instances' cache
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });

        // 7. Render final results
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        console.error("6 - Search error:", error); // Debug point 6
        if (error?.code === 20) return; // Ignore abort errors
        this.close(); // Handle real errors
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    // Only replace content if not showing recommendations

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");

    const countElement = this.querySelector(
      "[data-predictive-search-live-region-count-value]"
    );
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector(".section-header")?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    document.body.style.overflow = "hidden";
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");

      // recommendation - 4
      this.showingRecommendations = true;
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }
}

customElements.define("predictive-search", PredictiveSearch);
